import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toaster } from "@/components/Toaster";
import { formatDate, formFileDownloadByPathUrl, handleMutationError } from "@/lib/utils";
import { submitFinalDissertation } from "./dissertation.api";
import type { Dissertation } from "./dissertation.schema";
import { CheckCircle2, FileUp } from "lucide-react";

const FINAL_SUBMISSION_STATUS = 8;

export const FinalSubmissionCard: React.FC<{ dissertation: Dissertation }> = ({ dissertation }) => {
	const queryClient = useQueryClient();
	const [pdfFile, setPdfFile] = useState<File | undefined>();
	const [docxFile, setDocxFile] = useState<File | undefined>();
	// Remounts the file inputs so their internal file lists clear after a submission.
	const [inputsKey, setInputsKey] = useState(0);

	const mutation = useMutation({
		mutationFn: (files: { pdf: File; docx?: File }) => submitFinalDissertation(dissertation.key, files),
		onSuccess: () => {
			toaster("success", "Final dissertation submitted");
			setPdfFile(undefined);
			setDocxFile(undefined);
			setInputsKey((key) => key + 1);
			queryClient.invalidateQueries({ queryKey: ["dissertation"] });
			queryClient.invalidateQueries({ queryKey: ["dissertation-files"] });
		},
		onError: handleMutationError,
	});

	const inFinalStage = dissertation.status === FINAL_SUBMISSION_STATUS;

	if (!inFinalStage && !dissertation.has_final_dissertation) {
		return null;
	}

	const handleSubmit = () => {
		if (!pdfFile) {
			toaster("error", "Please attach the PDF of your final dissertation.");
			return;
		}

		mutation.mutate({ pdf: pdfFile, docx: docxFile });
	};

	if (dissertation.can_submit_final) {
		return (
			<div className="p-4 border-2 border-teal-200 bg-teal-50/40 rounded space-y-4">
				<div className="flex items-start gap-3">
					<FileUp className="h-5 w-5 text-teal-700 mt-1 shrink-0" />
					<div>
						<h3 className="font-semibold text-lg">Submit Final Dissertation</h3>
						<p className="text-sm text-gray-600 dark:text-gray-400">
							Your corrections have been approved. Upload the final version of your dissertation. Once submitted, your dissertation is marked as completed.
						</p>
					</div>
				</div>

				{dissertation.has_final_dissertation && (
					<p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
						Academic Affairs has re-opened submission. Your new upload will replace the final dissertation you submitted
						{dissertation.final_submitted_at ? ` on ${formatDate(dissertation.final_submitted_at, "Do MMM, YYYY")}` : ""}.
					</p>
				)}

				{dissertation.final_submission_remarks && (
					<div>
						<div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Remarks from Academic Affairs</div>
						<div className="text-sm mt-1">{dissertation.final_submission_remarks}</div>
					</div>
				)}

				{dissertation.final_submission_deadline && (
					<div>
						<div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Submission Deadline</div>
						<div className="text-sm mt-1">{formatDate(dissertation.final_submission_deadline, "Do MMM, YYYY")}</div>
					</div>
				)}

				<div key={inputsKey} className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div>
						<div className="text-sm font-medium mb-2">Final Dissertation (PDF) <span className="text-red-600">*</span></div>
						<Input
							type="file"
							allowedTypes=".pdf"
							subtitle="PDF Version"
							onChange={(event) => setPdfFile(event.target.files?.[0])}
							disabled={mutation.isPending}
							className="h-20 flex-row justify-between items-center"
							iconClassName="text-zinc-300 w-8"
						/>
					</div>
					<div>
						<div className="text-sm font-medium mb-2">Final Dissertation (Word, optional)</div>
						<Input
							type="file"
							allowedTypes=".docx"
							subtitle="Word Version"
							onChange={(event) => setDocxFile(event.target.files?.[0])}
							disabled={mutation.isPending}
							className="h-20 flex-row justify-between items-center"
							iconClassName="text-zinc-300 w-8"
						/>
					</div>
				</div>

				<div className="text-right">
					<Button type="button" onClick={handleSubmit} loading={mutation.isPending} disabled={!pdfFile || mutation.isPending}>
						Submit Final Dissertation
					</Button>
				</div>
			</div>
		);
	}

	if (inFinalStage) {
		return (
			<div className="p-4 border rounded bg-gray-50 dark:bg-neutral-800 space-y-1">
				<h3 className="font-semibold">Final Dissertation Submission</h3>
				<p className="text-sm text-gray-600 dark:text-gray-400">
					{dissertation.final_submission_deadline_passed
						? "The deadline for submitting your final dissertation has passed. Please contact Academic Affairs."
						: "Final dissertation submission is currently closed. Please contact Academic Affairs if you need to submit."}
				</p>
			</div>
		);
	}

	return (
		<div className="p-4 border border-emerald-200 bg-emerald-50/40 rounded flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
			<div className="flex items-start gap-3">
				<CheckCircle2 className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
				<div>
					<h3 className="font-semibold">Final Dissertation Submitted</h3>
					<p className="text-sm text-gray-600 dark:text-gray-400">
						{dissertation.final_submitted_at
							? `Submitted on ${formatDate(dissertation.final_submitted_at, "Do MMM, YYYY - h:mm a")}. `
							: ""}
						Please submit the final approved colour-bound copies to the College.
					</p>
				</div>
			</div>
			<a
				href={formFileDownloadByPathUrl(`dissertations/${dissertation.key}/final_dissertation_pdf`, dissertation.final_submitted_at ?? undefined)}
				className="text-sm text-blue-600 hover:underline shrink-0"
			>
				Download submitted PDF
			</a>
		</div>
	);
};

export default FinalSubmissionCard;
